import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardReferenceSchema,
} from './board';
import { ApiError } from './errors';
import { EVENT_TYPE_NAMES } from './events';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';
import { WORKSPACE_NAME_MAX_LENGTH } from './workspace';

/**
 * Notifications — the contract layer for block **B17**.
 *
 * `BE-17.4` and `BE-17.5` (`FR-NOTIF-005` the bell and its count,
 * `FR-NOTIF-006` the drawer listing and the retention window, `FR-NOTIF-007`
 * mark read and unread, `FR-NOTIF-009` what a click does). The *generation*
 * half — `BE-17.1`–`BE-17.3`, `FR-COLLAB-007` and `FR-NOTIF-001` — is
 * `./mention` plus `apps/server/src/notifications/generate.ts`, and nothing in
 * this file is on that path.
 *
 * ---------------------------------------------------------------------------
 * THE FIRST CONTRACT IN THIS PACKAGE WHOSE BLOCK CONSUMES THE EVENT SPINE
 * RATHER THAN CONTRIBUTING TO IT, AND THREE THINGS FOLLOW FROM THAT
 * ---------------------------------------------------------------------------
 * Every block from B06 to B16 answered *what may a user do*. B17 answers *who
 * has to be told*, and the inversion runs against this codebase's strongest
 * reflexes:
 *
 *   1. **There is no create body anywhere in this file.** No route creates a
 *      notification — they are derived inside `withTransaction`, on the same
 *      handle, from events five other blocks already emit. A
 *      `createNotificationBodySchema` would be a shape with no caller and an
 *      invitation to write notifications from a route, which is the design D1
 *      exists to refuse.
 *   2. **There is no `type` on any input schema.** The nine types below are a
 *      **view** vocabulary only. A caller never names one.
 *   3. **`notificationSchema` carries no `boardId`/`cardId` scalars — it
 *      carries nullable `board`, `card`, `workspace` and `comment` *refs*.** A
 *      drawer row must render its own text; an id would force `FE-17.3` into an
 *      N+1 fetch per row, and the objects it would fetch may no longer be
 *      readable (see the two absent conjuncts below).
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ TWO CONJUNCTS EVERY OTHER READ IN THIS SERVER CARRIES ARE ABSENT FROM
 * THIS ONE, AND BOTH ABSENCES ARE MANDATED BY ACCEPTANCE CRITERIA
 * ---------------------------------------------------------------------------
 * They are stated *here*, in the contract, because the schema is what makes
 * them expressible and a reader who only sees the route will file both as bugs.
 *
 * **(a) No board read scope, and no board scope of any kind.**
 * `FR-NOTIF-001` AC3: *"Given user A is removed from a board, when the removal
 * completes, then user A receives a notification whose text still renders even
 * though the board is no longer openable by them."* Composing
 * `isBoardReadableBy` into the listing would delete exactly the notification
 * type (`removed_from_board`) that most needs to arrive. **The recipient scope
 * IS the permission model of this route** — a notification was addressed to one
 * account, and that account may read it. There is no second question to ask,
 * and adding one would be the block's most expensive mistake.
 *
 * **(b) No `deleted_at` filter and no `INNER JOIN` behind any ref.**
 * `FR-NOTIF-006` AC4: *"Given a notification refers to a card that has since
 * been deleted, when the drawer renders it, then its text still displays and
 * its link is disabled rather than erroring."* An `INNER JOIN` would silently
 * **drop the row** rather than erroring — a 200 with a shorter array — so every
 * join is `LEFT`, and the refs carry a `deleted` flag instead of vanishing.
 * That is why `notificationBoardRefSchema` and `notificationCardRefSchema`
 * declare `deleted: boolean` where `boardSchema` deliberately declares no
 * `deletedAt` at all: on `boardSchema` the value could only ever be `null`
 * (every board read carries `deleted_at IS NULL`), and here it is genuinely
 * observable.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **Three of `ck_notifications_type`'s twenty-four values.** ⚠ **This
 *     bullet said "five of twenty-three" and both halves were stale; it is
 *     corrected in place, measured, rather than decremented.** The two
 *     `checklist_item_*` types (**B26**) it named as absent are in the tuple
 *     below and have been since B62, and `notification.test.ts`'s
 *     by-name assertion — which is the machine-checked half of this claim —
 *     has listed three, not five, for as long as that has been true. The
 *     twenty-third value became a twenty-fourth with
 *     `0062_webhook_signing_and_failure_notifications.sql`, whose
 *     `change_registration_failing` is registered below in the same change,
 *     so the arithmetic is 24 admitted − 21 registered = 3. The three are
 *     `invite_link_joined` (no invite-link route has shipped),
 *     `removed_from_workspace` (B07 registers no `DELETE` on a workspace
 *     member — verified against the routes, not assumed) and
 *     `automation_action_applied` (the automation domain). `./events` sets the
 *     precedent in as many words —
 *     *"the placeholders are still placeholders … and a block that first emits
 *     one registers it here then, not in advance."* ⚠ **`due_soon` left this
 *     list at B24 stage 2** — it is no longer absent, and it is no longer "not
 *     event-derived at all" either: it is still not derived from
 *     `NOTIFICATION_FROM_EVENT`, but it is now produced, by the sweep job named
 *     at its own array entry below. ⚠⚠ **The eight `watched_*` types left this
 *     list at B29**, for exactly the reason the bullet gave for keeping them
 *     out — *a block that first writes one registers it then* — and B29 is
 *     that block. They were described here as "structurally a different
 *     question: who subscribed rather than who was involved", and that
 *     description survives their registration unchanged: it is why the fan-out
 *     that writes them is a **fourth audience** (`watchers`) in
 *     `apps/server/src/notifications/generate.ts` rather than a widening of
 *     the three that were already there.
 *   - **A params schema.** `notificationParams` is `z.strictObject({ id:
 *     ulidSchema })` declared **at the route module**, which is where
 *     `commentParams`, `labelParams` and `cardParams` all live. Exporting one
 *     from here would be the first of its kind and would put a route's URL
 *     shape in a package the browser also imports.
 *   - ~~**A "mark all as read" body.** `FR-NOTIF-008` is **V1**. The route it
 *     will want is `POST /notifications/read-all`; pre-declaring its shape here
 *     would be a schema with no route, which is exactly the failure
 *     `./index`'s header spends a paragraph warning about.~~ ⚠ **B29 built that
 *     route, so the condition this bullet named is satisfied and the bullet is
 *     struck through rather than deleted** — it predicted its own expiry and is
 *     the clearest surviving statement of the rule that governed it.
 *     `markAllNotificationsReadResultSchema` is at the foot of this file, and
 *     it is a **result** rather than a body: the route still takes no request
 *     shape at all, which is *how* it complies with `FR-NOTIF-008`'s edge case
 *     (with the unread-only filter on, the action applies to **all**
 *     notifications rather than the filtered subset — a route that took a
 *     filter parameter could be asked to do the other thing).
 *   - **A `groupedCount` writer.** `FR-NOTIF-006`'s bulk-grouping edge case
 *     ("an automation touching fifty cards at once should collapse into one
 *     grouped row") belongs to the automation domain. `notifications.
 *     grouped_count` defaults to 1 and B17 writes it nowhere; the field is on
 *     the wire because the column is real and a client that renders "and 4
 *     others" should not need a schema change when it starts moving.
 *     ⚠ **`FE-17.3` re-points this bullet without changing it: the writer is
 *     still missing, and it is still not a contract gap.** The residual has
 *     been read as *"the wire always sends 1, so grouping is contract-blocked"*
 *     — it is not. `groupedCount` is declared here, projected by
 *     `routes/notifications.ts`, and **already rendered**:
 *     `apps/web/src/lib/notifications.ts` selects `message.forms.one` /
 *     `.other` on it and appends `notifications.groupedSuffix` (*"{base} and
 *     {count} others"*) when it exceeds 1. The contract is complete end to
 *     end. What is missing is one fold in
 *     `apps/server/src/notifications/generate.ts`, inside
 *     `generateNotifications(tx, events)` and **before** the single
 *     `tx.insert(notifications)`: the requirement's own words are "fifty cards
 *     **at once**", "at once" is one transaction, and that function already
 *     receives the transaction's whole event array in one call. Folding there
 *     writes ONE row with `grouped_count = 50`, after which the read-back, the
 *     per-id `PATCH …/read`, `GET /notifications/unread-count`, the cursor and
 *     the retention window all keep working unchanged.
 *     ⚠⚠ **Read-time collapse in the listing was designed out and refused**,
 *     although `activityEntrySchema.collapsedCount` and `./activity`'s
 *     collapser prove the shape is viable one table over. Three reasons, and
 *     each is a requirement rather than a preference: (i) `PATCH
 *     /notifications/:id/read` takes ONE id, so a row standing for fifty could
 *     only be toggled by fifty calls or by a new bulk route plus a
 *     `groupedIds` field — and without one of those `FR-NOTIF-007` AC1 ("the
 *     badge count decreases by one") is false on exactly the rows grouping
 *     created; (ii) `GET /notifications/unread-count` counts ROWS, so
 *     collapsing the listing and not the count produces a badge the drawer
 *     cannot account for, which is the divergence this block's route module
 *     already calls its quietest failure; (iii) the patch's 200 is a re-read
 *     through the listing's own projection and a single-row read cannot
 *     reproduce a page-scoped collapse.
 *   - **Notification row payloads over the live channel.** `FR-NOTIF-005`'s
 *     "updating live without a page reload" is now a content-free
 *     `notification.changed` signal in `./live`, not this view schema widened
 *     into a socket payload. The client refetches `GET /notifications` and
 *     `GET /notifications/unread-count`; it never applies notification rows
 *     from a WebSocket frame.
 */

// ---------------------------------------------------------------------------
// Caps and vocabularies.
// ---------------------------------------------------------------------------

/**
 * `notifications.type varchar(32)`.
 *
 * Asserted against the longest name below in `notification.test.ts`, exactly as
 * `EVENT_TYPE_MAX_LENGTH` is asserted against `EVENT_TYPE_NAMES` — the failure
 * without it is a `value too long for type character varying(32)` at INSERT,
 * inside a transaction that has already written its activity entry, so the
 * **mutation** fails rather than the notification.
 *
 * ⚠ **The headroom this docstring described is now spent, and the sentence is
 * corrected in place because the correction is the whole point of the
 * assertion.** Through B24 the widest *registered* name was
 * `made_workspace_admin` at 20, and `watched_card_created_on_board` at 29 was
 * quoted as the widest the constraint admits *at all* — a hypothetical ceiling.
 * B29 registers it, so 29 is now the widest name this package can produce and
 * the margin against the column is three characters. Nothing is wrong; what
 * changed is that a later block adding a longer name has almost no room left,
 * and it will discover that in a red test here rather than in production.
 */
export const NOTIFICATION_TYPE_MAX_LENGTH = 32;

/**
 * The **nine** notification types B17 produces, plus (⚠ see the two corrections
 * below) a **tenth** B24 stage 2 adds and **eight more** B29 adds.
 *
 * `ck_notifications_type` admits **twenty-three** — measured against the live
 * catalogue for this block, and every one of these nine is among them, which is
 * why B17 ships **no migration**. The other fourteen are listed in this file's
 * header with the block that owns each; the rule is `./events`', and it is the
 * one that keeps a vocabulary honest: a name is registered by the block that
 * first *writes* it, never in advance.
 *
 * ⚠ **`due_soon` has now left that "other fourteen" list — corrected in
 * place rather than deleted, because the header's own sentence just above
 * predicts exactly this.** `apps/server/src/notifications/due-reminder.ts`
 * (B24 stage 2, `BE-24.3`) is the writer, so the type is registered here now.
 * It is the **tenth** entry and the **first this file's own generator does
 * not produce** — see its own comment in the array below for what does.
 *
 * ⚠⚠ **THE EIGHT `watched_*` TYPES LEFT IT AT B29, AND THIS IS THE LARGEST
 * SINGLE ADDITION THIS TUPLE WILL EVER TAKE — corrected in place on the same
 * rule, and eighteen is now the count.** `FR-COLLAB-022`–`FR-COLLAB-024` and
 * `FR-NOTIF-002`: B29 writes the first `watches` row this product has ever had,
 * and `apps/server/src/notifications/watchers.ts` resolves it into a **fourth
 * audience** beside `payloadTarget`, `boardMembers` and `mentions`. Registering
 * them is not optional and not bookkeeping: `notificationSchema.type` is
 * `z.enum` of this tuple, so an unregistered `watched_comment_added` row would
 * make `GET /notifications` **500 on response serialisation** for exactly the
 * users the block was built for — a mutation that succeeded, a row that was
 * written, and a drawer that cannot be opened afterwards.
 *
 * ⚠ **What did NOT change with them, because a reader will assume it did.**
 * There are still only nine names between an event and a notification for these
 * eight (`NOTIFICATION_FROM_EVENT` in `apps/server/src/notifications/
 * generate.ts` maps twelve event types onto seven of them), and
 * `activity_entries` gains **nothing**: `ck_activity_entries_type` contains
 * zero watch-related names and must not gain one. `FR-COLLAB-022` makes
 * watching *"per-user and invisible to other members"*, and an
 * `activity_entries` row is the board's public record — a subscription nobody
 * else may observe cannot go in it. The event vocabulary and this one are two
 * different columns with two different CHECKs, and B29 widens neither.
 *
 * ⚠ **A `const` TUPLE, and emphatically NOT `Object.keys(…) as [string,
 * ...string[]]`.** That spelling infers `NotificationType = string`, which
 * silently voids the entire point of the union: `NOTIFICATION_FROM_EVENT` in
 * `apps/server/src/notifications/generate.ts` would accept
 * `notification: 'added_to_baord'`, it would compile, and it would fail
 * `ck_notifications_type` at runtime on the first card-member add — inside
 * `withTransaction`, so the **card member is not added either**. That map's
 * `satisfies Partial<Record<EventType, …>>` makes a typo in a **key** a compile
 * error; without the tuple here, a typo in a **value** is not, and the
 * asymmetry is invisible to a reader looking at the map alone.
 *
 * The order is the order `ck_notifications_type` declares them in, minus the
 * five absent ones, so a diff between this list and the constraint reads
 * top to bottom. `notification.test.ts` pins the count, the order and the
 * spelling; only the integration suite can compare against the live constraint.
 *
 * ⚠ **B29's eight append cleanly, and that is a property of the constraint
 * rather than luck.** `0006_notifications_and_personal_surfaces.sql` declares
 * the eight `watched_*` values **contiguously and immediately after
 * `due_soon`**, with only `automation_action_applied` after them — so "append
 * in the constraint's order" and "append to the end of the tuple" happen to be
 * the same edit here. They will not be for whoever registers
 * `removed_from_workspace` or the two `checklist_item_*` types, all three of
 * which belong **above** `due_soon`. Read the constraint before adding a name;
 * do not infer the position from this block's.
 *
 * ⚠ **`change_registration_failing` (FR-AUTO-034) appends cleanly for the same
 * reason and by the same accident, and the reason is worth stating because it
 * is the last time it will be true by luck.**
 * `0062_webhook_signing_and_failure_notifications.sql` rebuilds
 * `ck_notifications_type` from the live catalogue and `array_append`s the new
 * name, so it is the constraint's **last** value; the only unregistered name
 * now below the `watched_*` group is `automation_action_applied`, exactly as
 * it was for B29. So "the constraint's order" and "the end of this tuple"
 * coincide a second time. The rule did not change: read the constraint.
 */
export const NOTIFICATION_TYPE_NAMES = [
  // The event each one is derived from, and the block that emits it. The map
  // itself is `NOTIFICATION_FROM_EVENT` in `apps/server/src/notifications/
  // generate.ts` — this comment is the reader's index into it, not a second
  // copy: nothing here is machine-checked, and the map is what runs.
  //
  // ⚠ `member.self_joined` and `member.self_left` (B14) map to NOTHING, by
  // absence and on purpose. The actor IS the target, so `FR-NOTIF-004` would
  // drop the row on the next line; generating and then filtering is a slower
  // way to write the same nothing. B14 made that split load-bearing and
  // predicted this consumer — "being added by somebody else is a thing you are
  // told about, and joining a card yourself is not" — which is why they must
  // not be added here "for completeness".
  /** `comment.added` / `comment.edited` (B16) — resolved mentions in the body. */
  'mentioned',
  /** `member.added` on a card (B14). */
  'added_to_card',
  /** `member.removed` on a card (B14). */
  'removed_from_card',
  /** `board.member_added` (B10). */
  'added_to_board',
  /** `board.member_removed` (B10). */
  'removed_from_board',
  /** `board.member_role_changed` (B09) — **only** when the new role is admin. */
  'made_board_admin',
  /** `board.closed` (B08) — fanned out to every board member but the closer. */
  'board_closed',
  /** `workspace.member_added` (B07). */
  'added_to_workspace',
  /** `workspace.member_role_changed` (B07) — **only** when the new role is admin. */
  'made_workspace_admin',
  /** `checklist.item_assigned` (B62). */
  'checklist_item_assigned',
  /** `checklist.item_due_date_*` (B62). */
  'checklist_item_due_changed',
  /**
   * `ck_notifications_type`'s next member after `made_workspace_admin` once
   * `removed_from_workspace`, `invite_link_joined`, `checklist_item_assigned`
   * and `checklist_item_due_changed` — all four still unregistered — are
   * skipped, on this array's own stated rule (the constraint's order, minus
   * the absent ones).
   *
   * ⚠⚠ **B24 stage 2, and the FIRST type in this list this file's own
   * generator does not write.** `NOTIFICATION_FROM_EVENT` in
   * `apps/server/src/notifications/generate.ts` derives every type above
   * this one from an `activity_entries` row inside `withTransaction`; this
   * one is produced by `apps/server/src/notifications/due-reminder.ts`'s
   * scheduled sweep, which runs with no HTTP request, no mutation and no
   * event to derive from — `FR-NOTIF-003`'s "the reminder fires once per
   * card per user" is a clock condition, not an actor's gesture. Its row
   * therefore carries `activityEntryId: null`, the first legitimate null in
   * that column — `generate.ts`'s own header states an invariant that every
   * row IT writes sets that field, and this type is how that invariant stays
   * scoped to its writer rather than to the column.
   */
  'due_soon',

  // -------------------------------------------------------------------------
  // B29 — FR-COLLAB-022, FR-COLLAB-023, FR-COLLAB-024 and FR-NOTIF-002.
  //
  // ⚠ ALL EIGHT SHARE ONE AUDIENCE AND ONE RECIPIENT RULE, stated once here
  // rather than eight times below. The recipient set is
  //     (watches on the card ∪ watches on its CURRENT list ∪ watches on the
  //      board)  ∩  isInBoardAudience  \  {the actor}
  // resolved to a SET of user ids, never three concatenated queries — watching
  // a board AND a card inside it must produce ONE notification, which is
  // FR-COLLAB-023's AC3 and FR-NOTIF-002's first edge case stating the same
  // requirement twice. The isInBoardAudience conjunct is what makes
  // FR-NOTIF-002's last edge case true: a watcher who has lost board access
  // keeps the `watches` row (so regaining access restores the subscription)
  // and receives nothing while outside the audience.
  //
  // ⚠ TWO EVENT TYPES THAT LOOK LIKE THEY BELONG HERE AND DELIBERATELY DO NOT.
  //   - `card.position_changed` is NOT a move. A within-list reorder produces
  //     no notification at all: the product tie-breaker (a board must stay
  //     readable in ten seconds; where a feature makes it harder to read, the
  //     feature loses) applies to the bell as much as to the board face, and a
  //     drag-to-reorder storm would drown every other notification a watcher
  //     gets. Only the two cross-container moves are moves.
  //   - `comment.edited` gains no watch rule. A watcher hears about a NEW
  //     comment, not about somebody fixing a typo in an old one. ⚠ And
  //     `comment.added` keeps its `mentioned` rule as well as gaining this
  //     one — it is the first event in the product mapped to TWO
  //     notifications, which is why `NOTIFICATION_FROM_EVENT`'s value type is
  //     an array of rules rather than one rule.
  /** `comment.added` (B16) — a new comment on something the recipient watches. */
  'watched_comment_added',
  /**
   * `due.date_set`, `due.date_changed` and `due.date_removed` (B24 stage 1) —
   * **three** event names onto one notification, because a watcher's interest
   * is "the deadline moved", not which of the three shapes the move took.
   *
   * ⚠ **Due-date *approach* is NOT here and must not be added.** That is
   * `FR-NOTIF-003` / `due_soon`, ten entries above — already shipped by B24
   * stage 2, whose recipient set is `card_members ∪ watches`. It therefore
   * starts honouring watches the moment this block writes one, **with no code
   * change in `due-reminder.ts`**. `FR-COLLAB-022`'s prose lists "approach"
   * beside the changes, which is what makes a ninth `watched_*` type look
   * missing from the constraint; it is not missing, it is `due_soon`.
   */
  'watched_due_date_changed',
  /**
   * `due.date_completed` and `due.date_uncompleted` (B24 stage 1).
   *
   * `FR-NOTIF-002` names completion where `FR-COLLAB-022` does not — the two
   * prose lists are "close but not identical", and their **union minus
   * approach** is exactly the eight values `ck_notifications_type` already
   * held. The CHECK is the settlement of that difference rather than a
   * coincidence: the schema recorded the answer in `0006` before either list
   * was re-read.
   */
  'watched_completion_toggled',
  /** `attachment.added` (B27). `attachment.renamed`/`removed` are not watched. */
  'watched_attachment_added',
  /**
   * `card.moved_between_lists` and `card.moved_between_boards` (B12, B21).
   *
   * ⚠ For a cross-board move the **destination** list's watchers hear about it,
   * not the source's: `FR-COLLAB-023` scopes a list watch to "every card **in**
   * it", and after the move the card is in the destination. The fan-out reads
   * the card's **current** `list_id` from the `cards` table for exactly this
   * reason — `payload.listId` means different things on the two rows B21 writes
   * for one cross-board gesture, and several events do not carry it at all.
   */
  'watched_card_moved',
  /**
   * `card.archived` **and `card.unarchived`** (B22).
   *
   * ⚠⚠ **A NAMING COMPROMISE, RECORDED AS ONE RATHER THAN LEFT TO LOOK LIKE AN
   * ACCIDENT.** `FR-NOTIF-002`'s edge case requires the restore to notify —
   * *"archiving a watched card leaves the subscription intact so that restoring
   * it is itself notified"* — and there is no `watched_card_unarchived` value
   * in the constraint. Adding one would mean a migration widening
   * `ck_notifications_type`, which drags in the full per-name vocabulary ritual
   * (`docs/notes/migrations-and-events.md`) for zero behavioural gain. So this
   * value carries both directions and the **sentence** distinguishes them:
   * `apps/server/src/notifications/email/render.ts` says *restored* for the
   * `card.unarchived` row, at the only place a user ever sees the difference.
   * A client switching on `type` alone cannot tell them apart, and nothing in
   * the product asks it to.
   */
  'watched_card_archived',
  /**
   * `card.created` (B12), when the recipient watches the card's **list**.
   *
   * ⚠ **The one event whose notification type depends on which arm of the
   * watcher query matched, and the NARROWEST SCOPE WINS.** A user watching both
   * the list and its board gets **one** notification and it is this one — not
   * two, and not the board-scoped name. A freshly created card has no
   * card-scoped watchers, so only two of the three arms can match at all;
   * express the choice as `min(scope)` per user rather than as two fan-outs
   * whose results are concatenated, which would violate the dedupe rule stated
   * at the head of this group.
   */
  'watched_card_created_in_list',
  /**
   * `card.created` (B12), when the recipient watches the **board** and not the
   * list. See the entry above for why these two are one decision.
   *
   * ⚠ At 29 characters this is the widest name in the tuple and the widest
   * `ck_notifications_type` admits at all — three characters short of
   * `notifications.type varchar(32)`. See `NOTIFICATION_TYPE_MAX_LENGTH`: the
   * headroom that constant's docstring described is now spent.
   */
  'watched_card_created_on_board',

  // -------------------------------------------------------------------------
  // FR-AUTO-034 — the change-registration failure alert.
  // -------------------------------------------------------------------------
  /**
   * A change registration (the product's name for an outbound webhook) has
   * flipped to `failing`, told to the member whose grant authorises it.
   *
   * ⚠ **DERIVED FROM NO EVENT, AND THE SECOND TYPE IN THIS TUPLE THAT IS NOT
   * WRITTEN BY `generate.ts`.** `due_soon` was the first (a clock condition,
   * swept by `../../apps/server/src/notifications/due-reminder.ts`); this one
   * is written by `apps/server/src/external/change-registration-alerts.ts`,
   * called from the **delivery sweep** — a BullMQ job with no HTTP request, no
   * mutation, and no `activity_entries` row behind it.
   * `ck_activity_entries_type` gains nothing: registration CRUD emits no event
   * at all (`grep -c 'emit(' apps/server/src/external/change-registrations.ts`
   * → 0), and a delivery's *failure* is not a change to the board that anybody
   * else may observe.
   *
   * ⚠ **Its row therefore carries `activityEntryId: null`** — the second
   * legitimate null in that column after `due_soon`'s, and the reason
   * `generate.ts`'s "every row I write sets that field" invariant is scoped to
   * its writer rather than to the column. A reader who finds a null there and
   * files it as a bug is reading this tuple's entry too late.
   *
   * ⚠ **The sweep, not the route, is the writer.** A member disabling their
   * own registration is not told anything — they did it. The alert exists for
   * the case nobody is watching: `FR-AUTO-034` disables a registration that
   * keeps failing, and before this type the only trace was a `last_error`
   * string on a surface the member has no reason to open. The notification is
   * what makes "an API way to re-enable it" a thing a member can ever reach.
   *
   * ⚠ **No card, no board, no workspace ref, and no actor.** Nothing in the
   * `notifications` ref set addresses a change registration, so every ref on
   * this row is null and `notificationLink()`
   * (`apps/server/src/notifications/email/render.ts`) answers `null` for it —
   * a drawer row and an email with no deep link. Giving it the registration's
   * *subject* board as a ref would send the reader to a board face that says
   * nothing about their integration. Whoever builds the registration surface
   * as a routed page can revisit that; it needs a ref vocabulary this table
   * does not have.
   */
  'change_registration_failing',
] as const;

/**
 * `z.enum` over the vocabulary — `commentingPermissionSchema`'s and
 * `labelColourSchema`'s call for the same class of column (a CHECK-constrained
 * `varchar`), and for the same reason: an out-of-vocabulary value here means
 * this list and the constraint have **drifted**, which is something a read
 * should surface rather than pass through. It cannot mean "stale data a reader
 * should tolerate", because the CHECK refuses such a row at INSERT.
 */
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPE_NAMES);

export type NotificationType = z.infer<typeof notificationTypeSchema>;

/**
 * **D5's answer, and `09-notifications-and-personal-surfaces.md`'s open
 * question closed in place.**
 *
 * `00-overview-and-mvp-scope.md` commits to "an explicit window (90 days) with
 * a visible end-of-history state" as a deliberate divergence from Trello, whose
 * notification history simply stops without saying so.
 *
 * ⚠ **It is a READ-TIME FILTER, NOT A PURGE JOB**, and the difference is
 * reversibility: a filter loses no row and can be widened by changing this
 * number, while a purge needs a scheduler, a dead-letter story and an operator
 * runbook that no requirement asks for — and it destroys the audit trail
 * `notifications.activity_entry_id` was designed to preserve.
 *
 * ⚠ **THE INVARIANT THAT FAILS SILENTLY: the unread COUNT must apply the SAME
 * window as the LISTING.** If they diverge, the badge shows a number the drawer
 * cannot account for — a 200 on both surfaces, well-formed bodies, and nothing
 * anywhere that says so. The route module spells the predicate **once**
 * (`withinRetentionWindow()`) and both queries call it; the assertion that
 * catches a divergence has to check both **in one test**, because two separate
 * tests can be fixed apart.
 *
 * **Declared here rather than on the server** because `FE-17.4` renders the
 * end-of-history state ("Notifications older than 90 days are not kept") and a
 * hard-coded second 90 in the client is a number that can drift from the one in
 * the `WHERE`.
 */
export const NOTIFICATION_RETENTION_DAYS = 90;

/**
 * How many notifications `GET /notifications` returns when the caller sends no
 * `?limit`.
 *
 * 25, matching `COMMENT_PAGE_SIZE_DEFAULT` — a drawer's first render should
 * need one round trip. ⚠ Tests must pass `?limit=` explicitly rather than
 * depending on this value, or they assert the constant instead of the behaviour
 * and move when it moves. That is `./comment`'s stated rule, inherited rather
 * than re-derived; **the two constants are NOT shared**, on `./card`'s rule —
 * import the constant describing your own surface, declare it if none exists,
 * and never share one across two.
 */
export const NOTIFICATION_PAGE_SIZE_DEFAULT = 25;

/**
 * The ceiling on `?limit`.
 *
 * ⚠ **Notifications are the second unbounded collection in the product**, after
 * comments — an active account accumulates them for ever, or at least for
 * `NOTIFICATION_RETENTION_DAYS`. Without this, `?limit=1000000` is a legal
 * request that reads ninety days of history into memory and serialises it with
 * five joins attached. The value that matters for correctness is that there
 * *is* one.
 */
export const NOTIFICATION_PAGE_SIZE_MAX = 100;

/**
 * How much of a mentioning comment a drawer row carries.
 *
 * ⚠ **A PROJECTION bound, not a column bound, and deliberately NOT
 * `COMMENT_BODY_MAX_LENGTH`.** A comment body is capped at 16,384; twenty-five
 * drawer rows at 16 KB each is 400 KB per bell click, for a surface that
 * renders one line. The excerpt is computed as `left(comments.body, 280)` **in
 * SQL**, so the other 16 KB never leaves Postgres — `resolveComment`'s
 * `char_length` argument applied to a read path instead of a refusal path.
 *
 * 280 rather than a round 256: it is a recognisable "one line of prose" budget
 * and nothing in the product depends on the exact figure. `truncated` on the
 * ref is `char_length(body) > 280`, so the client renders an ellipsis rather
 * than guessing from the length.
 */
export const NOTIFICATION_COMMENT_EXCERPT_MAX_LENGTH = 280;

// ---------------------------------------------------------------------------
// Views — the refs a drawer row hangs its text on.
//
// Every one is nullable on the wire (the column behind it is nullable, and a
// workspace-scoped notification has no board or card at all), and every object
// ref carries `deleted` rather than collapsing to null. See the header's
// clause (b): `FR-NOTIF-006` AC4 needs the row's TEXT to still render, and
// "Ada added you to Sprint 4" needs the title of a card that may since have
// been deleted. `null` would make the row unreadable; `deleted: true` is what
// `FE-17.3` disables the link on. The recipient was already told about that
// object, so returning its title discloses nothing new.
// ---------------------------------------------------------------------------

/**
 * The board a notification is about — `board_id`, `LEFT JOIN boards`.
 *
 * `closed` **and** `deleted` are both here and they are different states:
 * `board_closed` notifications are *about* the first, and the second is the
 * `FR-NOTIF-006` AC4 case. Neither is a reason to hide the row.
 *
 * ⚠ **This is not `boardSchema` narrowed, and it must not become
 * `boardSchema`.** That shape carries `workspaceId`, `slug` and `visibility`,
 * serialises eight shipped responses, and — decisively — is produced by reads
 * that all carry `deleted_at IS NULL`, so it has nowhere to put `deleted`.
 * Nesting it here would couple a drawer row to every future widening of the
 * board view and would put a `visibility` on a board the recipient may no
 * longer read.
 */
export const notificationBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  /**
   * `FR-NOTIF-009`: "the notification opens the exact card" — and for a
   * board-scoped notification, the exact board. The **short link** rather than
   * the ULID because that is the permalink the product already has
   * (`FR-EXP-015`), and it survives everything an id would.
   *
   * ⚠ **No `slug` and therefore no assembled `path`.** A slug is derived from
   * the *current* title at read time, and a notification's link is allowed to
   * be stale in a way a board read is not: `/b/<shortLink>` resolves without
   * one, and `FR-EXP-015` AC1 has the server correct the address on arrival.
   * Carrying a slug would put a second, staler copy of the title on the wire.
   */
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /** `boards.closed_at IS NOT NULL` — `boardSchema.closed`'s derivation. */
  closed: z.boolean(),
  /** `boards.deleted_at IS NOT NULL`. See the header, clause (b). */
  deleted: z.boolean(),
  /**
   * Whether **the recipient of this notification** can still read the board —
   * `FR-NOTIF-009` AC3, `FE-17.4`. Composed from
   * `apps/server/src/boards/permissions.ts`'s `isBoardReadableBy(userId)`,
   * **never restated**, and projected as a boolean.
   *
   * ⚠⚠ **A FLAG, NEVER A FILTER, AND THE DIFFERENCE IS A REQUIREMENT.**
   * `FR-NOTIF-001` AC3 requires the *removed from the board* notification to
   * arrive and render; `FR-NOTIF-009` AC3 then requires the click to say
   * *"you no longer have access"* rather than erroring. Composing the read
   * scope into the listing's `WHERE` — which is what this field looks like an
   * invitation to do — deletes exactly the row both ACs are about, with a 200
   * and a shorter page. `routes/notifications.ts`'s module header calls that
   * "the block's most expensive mistake"; this field exists so a client can
   * classify without the query ever narrowing.
   *
   * ⚠ **`deleted` did NOT already answer this, and that is the whole point of
   * `FE-17.4`.** Before this field, "the board was deleted" and "you were
   * removed from the board" were indistinguishable on the wire: `closed` and
   * `deleted` are facts about the *board*, and neither says anything about
   * *this reader*. A former member of a live board sees `closed: false,
   * deleted: false` and cannot open it.
   *
   * ⚠ **The `boards.deleted_at IS NULL` conjunct is written EXPLICITLY beside
   * the composed predicate, and leaving it out is silent.** `isBoardReadableBy`
   * does **not** carry `isLiveBoard()` — its own usage example is
   * `and(isLiveBoard(), isBoardReadableBy(userId))` — so a soft-deleted board
   * whose membership row still matches would project `readable: true` beside
   * `deleted: true`, and a client trusting this flag would offer a link into a
   * 404.
   *
   * A **closed** board stays `readable: true`. Closing is reversible and a
   * closed board is legitimately openable by anyone who may read it; `closed`
   * is already its own flag and the two must not be conflated.
   *
   * Non-nullable: an omitted flag would read as `false` in every client that
   * treats it as a boolean, which turns a perfectly readable board into "you
   * no longer have access" — the failure in the direction that loses the user
   * their own data.
   */
  readable: z.boolean(),
});

export type NotificationBoardRef = z.infer<typeof notificationBoardRefSchema>;

/**
 * The card a notification is about — `card_id`, `LEFT JOIN cards`.
 *
 * ⚠ **No `archived` flag, deliberately, although `cards.archived_at` exists.**
 * The two states differ in what a client does: a deleted card cannot be opened,
 * so `FE-17.3` disables the link, while an **archived** card is still reachable
 * by permalink — B14's card back is explicit that a card whose list has been
 * archived stays openable and merely *indicates* the fact. So an archived card
 * needs no flag here; its own back renders the banner. Adding one would put a
 * second renderer of the same state on a surface that cannot act on it.
 */
export const notificationCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  /**
   * The card's reference (`MG-0001`) for **this reader**, or `null` — an inbox
   * card, a deleted one, or a reader outside the reference scope
   * (`isBoardReferenceableBy`). A client links `/c/<ref ?? shortLink>`, i.e.
   * `cardPath()` in `./address`. `docs/notes/card-keys.md`, "Wire".
   */
  ref: cardReferenceSchema.nullable(),
  /** `cards.deleted_at IS NOT NULL`. */
  deleted: z.boolean(),
});

export type NotificationCardRef = z.infer<typeof notificationCardRefSchema>;

/**
 * The workspace a notification is about — `workspace_id`, `LEFT JOIN
 * workspaces`.
 *
 * ⚠ **It is populated on EVERY notification this block writes, not only on the
 * two workspace-scoped types.** `payload.workspaceId` rides on all ten mapped
 * events (`activity_entries` has no `workspace_id` column, so `0012` put the id
 * in the payload; `notifications` *does* have the column, and B17 is the first
 * consumer to benefit). A client grouping the drawer by workspace therefore
 * needs no second query — and `added_to_workspace` / `made_workspace_admin` are
 * the only two rows where `board` and `card` are both null.
 */
export const notificationWorkspaceRefSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  /** `workspaces.deleted_at IS NOT NULL`. */
  deleted: z.boolean(),
});

export type NotificationWorkspaceRef = z.infer<typeof notificationWorkspaceRefSchema>;

/**
 * The comment a `mentioned` notification came from — `comment_id`, `LEFT JOIN
 * comments`.
 *
 * ⚠ **A REF CARRYING AN EXCERPT, NOT A BARE `commentId`, and that is a
 * correction to the obvious shape.** `FR-NOTIF-001` AC1 is explicit: "user A
 * has a new unread notification naming the commenter, the card **and the
 * comment text**." A bare id forces `FE-17.3` into an N+1 fetch per drawer row
 * and leaves `FR-NOTIF-009`'s inline reply box with nothing to quote. Nothing
 * would redden if this were an id — there is no frontend — so the gap would
 * surface much later as either an N+1 or a schema widening designed by somebody
 * who never read AC1.
 *
 * ⚠ **NO `deleted` FLAG, and this is the one ref that differs — read it beside
 * the other three rather than harmonising it.** `FR-COLLAB-003`'s delete is a
 * **hard** delete: `comments` has no `deleted_at` column at all, and
 * `fk_notifications_comment_id` is `ON DELETE SET NULL`. So the column goes
 * null and **`comment: null` IS the deleted state** — a state an integration
 * test can construct today, unlike the board and card cascades, which are
 * dormant because both of those are soft-deleted. A `deleted: true` here would
 * be unreachable.
 *
 * ⚠ **This does NOT breach B16's payload rule, and the two must not be
 * conflated.** That rule forbids copying a body into `activity_entries.payload`
 * — an append-only row nothing prunes, readable by anyone with the link on a
 * public board, which would make `FR-COLLAB-003`'s promise that deletion is
 * permanent false. This excerpt is a **join against the live `comments` row**:
 * it disappears the moment the hard delete removes it. Nothing is copied and
 * nothing outlives the comment.
 *
 * **Consequence, stated because it is the half a reader discovers later:** the
 * excerpt is **current, not historical**. Edit the comment and the drawer shows
 * the new text. That is the same call — a join, never a snapshot — and it is
 * the only one consistent with keeping no copy anywhere.
 */
export const notificationCommentRefSchema = z.object({
  id: ulidSchema,
  /**
   * `left(comments.body, 280)`.
   *
   * ⚠ **Bounded here although `commentSchema.body` deliberately is NOT.** That
   * field is unbounded because `comments.body` is `text` and a *view* that
   * enforced a product cap would 500 on a legitimate row. This one is different
   * in kind: the bound is the **projection's own**, applied by the query that
   * produces the value, so a string longer than 280 here means the `left(…)`
   * was dropped — which is the exact regression the bound exists to catch,
   * because 400 KB of drawer is otherwise indistinguishable from a slow client.
   */
  excerpt: z.string().max(NOTIFICATION_COMMENT_EXCERPT_MAX_LENGTH),
  /** `char_length(comments.body) > 280` — the client renders the ellipsis. */
  truncated: z.boolean(),
});

export type NotificationCommentRef = z.infer<typeof notificationCommentRefSchema>;

/**
 * One notification, as `GET /notifications` and `PATCH /notifications/:id/read`
 * both return it.
 *
 * One view for both, rather than a narrower shape for the patch, because they
 * answer the same question — *what is this notification now* — and the patch's
 * 200 body is a **re-read through the listing's own projection**, never an echo
 * of the request. `labelSchema`'s and `commentSchema`'s argument.
 *
 * ⚠ **`read: boolean`, not `readAt`.** `FR-NOTIF-007` names two states and no
 * surface renders a timestamp. The column keeps the **first** read time —
 * `coalesce(read_at, now())` in the `UPDATE`, which is what makes a double
 * click idempotent — so whoever later wants "read 3 hours ago" has the value;
 * shipping it now would be a promise to keep it meaningful before anything
 * needs it. This is `listSchema.archived`'s and `boardSchema.closed`'s
 * derivation, and the deliberate opposite of `commentSchema.editedAt`, which is
 * a timestamp on the wire precisely because `FR-COLLAB-006` renders one.
 *
 * ⚠ **THE DERIVED TYPE `Notification` COLLIDES WITH THE DOM'S GLOBAL
 * `Notification`, AND IT COLLIDES IN EXACTLY THIS BLOCK'S DOMAIN.** `apps/web`
 * compiles with the DOM lib, so `Notification` there is the Web Notifications
 * API constructor unless something imports this one — and `FE-17.2` renders a
 * bell, which is the single most likely file in the product to want both. The
 * symptom is a bewildering type error on `new Notification('…')` in a module
 * that happens to import this type, or an editor auto-import quietly pulling in
 * the wrong `Notification`.
 *
 * **Not renamed**, because every type in this package is `z.infer` of the schema
 * beside it under the schema's own name, and a `MoltNotification` here would be
 * the only exception in ten files — a bigger surprise than the collision. The
 * fix at the call site is a one-word alias (`import type { Notification as
 * NotificationRow }`) or `globalThis.Notification` for the browser API. Recorded
 * here because it is invisible from the server, where there is no DOM lib and
 * nothing ever collides.
 */
export const notificationSchema = z.object({
  id: ulidSchema,
  type: notificationTypeSchema,
  /** `notifications.read_at IS NOT NULL`. */
  read: z.boolean(),
  /**
   * `notifications.created_at timestamptz(3) NOT NULL DEFAULT now()`.
   *
   * ⚠ **A bounded-below string and emphatically NOT `z.iso.datetime()`.** `pg`
   * hands a `timestamptz` back in Postgres' own spelling
   * (`2026-08-12 12:00:00.123+00` — a **space** rather than a `T`, an offset
   * rather than a `Z`), which is a legitimate instant and not an ISO 8601 form.
   * Every timestamp field in this package spells it this way and every one of
   * their docstrings names `z.iso.datetime()` as the rejected alternative; the
   * failure would be a **500 on every drawer open**, not a compile error.
   *
   * It is also the **sort key and half the cursor** — `ORDER BY created_at
   * DESC, id DESC` — so it must be the exact string the driver returned, or the
   * cursor it is encoded into names a row the next page cannot find.
   */
  createdAt: z.string().min(1),
  /**
   * `notifications.grouped_count integer NOT NULL DEFAULT 1`.
   *
   * `.min(1)` because a grouped row always represents at least the
   * notification itself.
   *
   * ⚠ **THIS FIELD NOW HAS A PRODUCER, AND THE REVERSAL IS RECORDED RATHER
   * THAN THE OLD SENTENCE DELETED.** It used to read *"Always `1` today — B17
   * writes it nowhere and the column default supplies it"*, and it was true
   * for as long as `FE-17.3` had no writer. The fan-out 9 chunk *Notification
   * Fold* wrote one: `apps/server/src/notifications/generate.ts`'s `foldRows`
   * collapses the rows one transaction collected by
   * `(recipientUserId, type, actorUserId, boardId)` **before** the single
   * `tx.insert(notifications)`, keeps the last member and sets this to the run
   * length. `FR-NOTIF-006`'s edge case — *"an automation touching fifty cards
   * at once should collapse into one grouped row rather than fifty"* — is what
   * that implements, and "at once" is one transaction.
   *
   * ⚠ **The shape did not change and could not have needed to.** This field
   * was declared, projected and rendered before the writer existed, which is
   * why the fold cost no schema change at all: `notifications.grouped_count` is
   * `integer NOT NULL DEFAULT 1`, so a value above 1 is a number a shipped
   * client was already reading. Rows written before the fold read back as `1`
   * and are indistinguishable from singletons written after it — there is no
   * backfill and no migration.
   *
   * ⚠ **A folded row summarises its members away, and a client must not
   * assume otherwise.** `card`, `comment` and `activityEntryId` belong to the
   * **surviving** member, not to the group: a row with `groupedCount: 50`
   * links to one of the fifty cards. That is `FR-NOTIF-006`'s own trade — see
   * `foldRows` for why keying on the card would collapse nothing.
   */
  groupedCount: z.number().int().min(1),
  /**
   * Who caused it, or `null` when nobody can be named.
   *
   * `publicProfileSchema.nullable()`, reused whole — `commentSchema.author`'s
   * and `cardMemberSchema`'s call, so an actor cannot acquire an `email` by
   * accident and a field added to a public profile appears everywhere at once.
   *
   * ⚠ **Four ways it is legitimately null, and none of them is an error —
   * one more than this file counted through B23, corrected in place.** The
   * first three: `fk_notifications_actor_user_id` is `ON DELETE SET NULL`,
   * so a deleted account leaves the row standing; an automation actor is
   * null by design (`automation_command_id` is the other nullable FK on
   * this table); and the `users` join is `LEFT`, never `INNER`, for the
   * header's clause (b) reason. **The fourth, B24 stage 2's `due_soon`, is a
   * different KIND of null from the first three**: those three are null
   * because a real actor's identity became unavailable after the fact (an
   * account gone, a role with none, a join that did not match); a scheduled
   * reminder is null because **there never was an actor** — nobody did
   * anything, a clock condition was met. It is the first row in this column
   * that is null **at write time, by design, for a non-automation row**. The
   * server invents **no** placeholder name — a server-side fallback string
   * would be an untranslatable sentence built by concatenation, which
   * `FR-COLLAB-018`'s edge case forbids by name. The client renders the gap.
   */
  actor: publicProfileSchema.nullable(),
  board: notificationBoardRefSchema.nullable(),
  card: notificationCardRefSchema.nullable(),
  workspace: notificationWorkspaceRefSchema.nullable(),
  comment: notificationCommentRefSchema.nullable(),
  /**
   * `notifications.activity_entry_id char(26)` — the activity entry this
   * notification was derived from, or `null`.
   *
   * ⚠ **THIS REVERSES A DECISION THIS FILE'S ROUTE MODULE STATED IN AS MANY
   * WORDS, and the reversal is recorded here rather than the old sentence
   * deleted.** `NOTIFICATION_VIEW_COLUMNS` in
   * `apps/server/src/routes/notifications.ts` excluded the column as *"a
   * provenance column for a debugger, not a field a client has any use for"*.
   * That was true when it was written, because there was no client.
   * `FR-NOTIF-009`'s Behaviour is verbatim: the notification *"opens the card
   * detail it refers to, **scrolled to the relevant comment or activity
   * entry** where applicable"* — and `activityAnchor(entry.id)` already ships
   * in this package and is already what `ActivityEntryItem` renders its `id`
   * from. Without this field the "or activity entry" half of AC-behaviour has
   * no id to scroll to, and the drawer can only ever anchor on a comment.
   *
   * ⚠ **Not an invention: `object-model.ts`'s `notificationObjectSchema` has
   * declared `activityEntryId` all along.** The canonical object model has
   * always said a notification *has* an activity entry; only the route
   * projection withheld it, so this is the view catching up with the model
   * rather than a new fact about the domain.
   *
   * **Null on exactly two kinds of row**, and both are legitimate: the
   * scheduled sweeps write `activityEntryId: null` because nobody acted and
   * there is no entry (`notifications/due-reminder.ts`,
   * `notifications/checklist-item-due-reminder.ts`), and
   * `fk_notifications_activity_entry_id` is `ON DELETE SET NULL`, so a purged
   * entry leaves the notification standing. Every row
   * `notifications/generate.ts` writes carries one.
   *
   * ⚠ Projected from **`notifications.activity_entry_id`, the column** — never
   * from the joined `activity_entries.id`. The two differ for exactly one row
   * shape (an entry deleted between the two reads) and taking it off the join
   * would make this field disagree with `sourceEventType` and `via`, which are
   * the join's.
   */
  activityEntryId: ulidSchema.nullable(),
  /**
   * `activity_entries.type` for `activityEntryId`, or `null` when there is no
   * entry — the **event** that caused this notification, as distinct from the
   * notification's own `type`.
   *
   * ⚠⚠ **THIS IS NOT A SECOND ENCODING OF `type`, AND SHIPPING IT CLOSES A
   * LIVE DEFECT IN WHICH THE DRAWER AND THE EMAIL DESCRIBE ONE ROW
   * DIFFERENTLY.** `notifications/generate.ts` maps **both** `card.archived`
   * and `card.unarchived` onto the single notification type
   * `watched_card_archived`, deliberately — there are eight `watched_*` types
   * for far more than eight events. `NOTIFICATION_SOURCE_EVENT_TEMPLATE_
   * OVERRIDES` in `./interface-language` exists to tell such rows apart and
   * holds `'card.unarchived': '{actor} restored {card}'` today. The **email**
   * path already resolves it — `notifications/email/send.ts` selects
   * `sourceEventType: activityEntries.type` over the identical
   * `activity_entry_id` LEFT JOIN, and its integration suite asserts that a
   * restored card says RESTORED and an archived one says ARCHIVED from the same
   * notification type. The drawer could not, because this field was not on the
   * wire: `apps/web/src/lib/notifications.ts` reaches for it through an
   * `as unknown as { sourceEventType?: unknown }` cast that has therefore
   * always yielded `undefined`. **Restore a watched card and the member's
   * email says "restored" while the drawer says "archived", from one row.**
   *
   * `z.enum(EVENT_TYPE_NAMES)` — the **whole** registry, exactly as
   * `activityEntrySchema.type` spells it, and never `FEED_EVENT_TYPES`: a
   * notification may be derived from an entry that does not surface in the
   * feed, and narrowing here would 500 the listing on a row the database
   * legitimately holds. Containment measured rather than assumed:
   * `EVENT_TYPE_NAMES` is a strict superset of `ck_activity_entries_type`'s
   * values, so no stored entry type can fail this enum on serialisation.
   *
   * ⚠ **A client must never branch on this INSTEAD of `type`.** `type` is the
   * vocabulary the preferences, the categories and the badge are keyed on;
   * this is a *rendering* refinement of the sentence, and the override table is
   * the only thing that should consult it.
   */
  sourceEventType: z.enum(EVENT_TYPE_NAMES).nullable(),
  /**
   * `activity_entries.actor_automation_name` for `activityEntryId` — the name
   * of the automation or integration that caused this notification, or `null`
   * when a person did (or when there is no entry).
   *
   * `FE-70.4`, and `FR-COLLAB-017`'s edge case: actions by an automation are
   * attributed *"to that automation/integration by name, never to a blank
   * actor"*. This is the notification-side half of the fact
   * `activityEntrySchema.actorAutomationName` already carries on the feed, and
   * it is spelt the same way — bare `z.string().nullable()`, no `.max()`,
   * matching that field exactly, because both project the same
   * `varchar(255)` column and an asymmetric bound between two views of one
   * column is a 500 on whichever one is narrower.
   *
   * ⚠ **`actor` and `via` are INDEPENDENT and a client must render both.**
   * `actor` stays populated when an automation acted **on a member's behalf**
   * (`FR-AUTO-016` AC3: *"the comment appears under the acting member's name
   * and avatar and is distinguishable from a comment they typed"*), so
   * `actor !== null && via !== null` is the ordinary shape of a rule-run row,
   * not a contradiction. `ACTIVITY_VIA_TEMPLATE` in `./activity` —
   * `'{sentence}, via {tool}'` — is how the two are composed, by the client;
   * the server builds **no** concatenated sentence, which `FR-COLLAB-018`'s
   * edge case forbids by name.
   *
   * ⚠ **NOT `notifications.automation_command_id`, although that column is
   * real and indexed.** Nothing writes it — `buildRow` in
   * `notifications/generate.ts` does not set it and no other writer exists — so
   * putting it on the wire would be a field with no producer, which this
   * file's header argues against twice. The fact lives in `activity_entries`
   * and reaches here over a primary-key join.
   */
  via: z.string().nullable(),
});

export type Notification = z.infer<typeof notificationSchema>;

// ---------------------------------------------------------------------------
// Paging — the cursor, its codec, and the query.
//
// ⚠ DECLARATION ORDER IS LOAD-BEARING BELOW THIS LINE. `const` is not hoisted,
// so `notificationCursorSchema` must precede `notificationPageSchema` and
// `notificationQuerySchema`, both of which reference it. Reordered, the module
// throws a TDZ ReferenceError at import — every route 500s, and it fails at
// boot rather than in a test.
//
// ⚠ A SECOND CODEC RATHER THAN A SHARED GENERIC ONE, and D4's "reuse B16's
// cursor shape" is honoured by reusing the SHAPE, which is what `./comment`'s
// own docstring says is the reusable thing: opaque, value-carrying, base64url
// of `(created_at, id)`, `nextCursor: string | null`, 422 on malformed. A
// shared `keyset.ts` would couple two tables' sort keys — the day one changes,
// the other changes silently — and would mean editing a shipped, tested file
// for its second caller. `encodeCommentCursor`'s docstring also carries
// reasoning specific to `FR-COLLAB-003`'s hard delete that would have to be
// duplicated or lost. ⚠ RULE OF THREE: the block that wants a third keyset
// stream extracts it then, and B16 and B17 are its first two callers.
// ---------------------------------------------------------------------------

/**
 * An opaque paging cursor. Bounded, not structured — a client reads it from
 * `notificationPageSchema.nextCursor` and hands it back verbatim.
 *
 * 128 is comfortably above what `encodeNotificationCursor` produces (a Postgres
 * timestamp plus a ULID plus a separator is 53 bytes, so 71 base64url
 * characters) and low enough that a fabricated cursor cannot push a megabyte
 * through a query string.
 *
 * ⚠ **Bounded here and *decoded* separately.** This schema only says "a
 * plausible cursor-shaped string"; `decodeNotificationCursor` is what refuses a
 * malformed one, and it must be called — base64 decoding does not fail on
 * garbage.
 */
export const notificationCursorSchema = z.string().min(1).max(128);

/**
 * How Postgres renders a `timestamptz(3)`, permissively.
 *
 * ⚠ **THIS PATTERN IS THE DIFFERENCE BETWEEN A 422 AND A 500, AND IT IS A
 * DELIBERATE DIVERGENCE FROM `./comment`'s CODEC — READ IT BEFORE
 * "HARMONISING" THE TWO.** B16's `commentCursorPayloadSchema` bounds its
 * `createdAt` by **length only**, which leaves a hole its own docstring's
 * argument is about: a fabricated cursor carrying a well-shaped ULID and the
 * eight characters `notatime` decodes cleanly, splits into exactly two parts,
 * passes the ULID check, and reaches
 * `WHERE (created_at, id) < ('notatime'::timestamptz, …)` — where Postgres
 * raises `invalid input syntax for type timestamp with time zone` and the
 * caller gets a **500** instead of the 422 the whole codec exists to produce.
 * The length bound stops a megabyte; it does not stop eight bytes of nonsense.
 *
 * This block closes it here rather than in `./comment` because a shipped,
 * tested file should not be edited for its second caller's benefit — and
 * because declaring a second codec was justified on exactly this ground: the
 * two are allowed to diverge, and this is the first place they do.
 *
 * **Measured against the live database rather than reconstructed**, which is
 * what makes the pattern safe to be strict about: `now()::timestamptz(3)::text`
 * renders `2026-08-12 03:38:59.462+00`, and the fractional part is **omitted
 * entirely** when it is zero (`2026-01-01 00:00:00+00`). The offset is the
 * session's `TimeZone`, so `+00` here and `-05` or `+05:30` under a client that
 * set one. The `T`/`Z` alternatives are admitted because an ISO string is a
 * legitimate thing for a caller to have round-tripped through a `Date`, and
 * Postgres casts it identically.
 *
 * ⚠ It validates the **shape**, not the calendar: `9999-99-99 99:99:99+00`
 * passes here and would still be a 500. Closing that would mean parsing dates
 * in a package that must not depend on a date library, and the residual hole is
 * a fabricated cursor that is *nearly* a timestamp — vastly narrower than the
 * one above, which any typo produces.
 */
const POSTGRES_TIMESTAMPTZ =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d{1,6})?([+-]\d{2}(:\d{2}(:\d{2})?)?|Z)?$/;

/**
 * The decoded cursor: the exact `(created_at, id)` pair of the last
 * notification on the page that was served.
 *
 * Module-private on purpose — a caller receives this shape from
 * `decodeNotificationCursor` and never constructs one from a cursor by hand.
 * `createdAt` is bounded at 64 **and** matched against `POSTGRES_TIMESTAMPTZ`;
 * the length bound stops a fabricated cursor smuggling a long string into a
 * query parameter, and the pattern stops a short one reaching the `::timestamptz`
 * cast. A `timestamptz(3)` renders as about 26 characters.
 */
const notificationCursorPayloadSchema = z.strictObject({
  createdAt: z.string().min(1).max(64).regex(POSTGRES_TIMESTAMPTZ),
  id: ulidSchema,
});

/**
 * The base64url alphabet, unpadded.
 *
 * ⚠ **Hand-rolled rather than `btoa`/`atob` or `Buffer`, and both alternatives
 * were rejected for measured reasons rather than taste** — `./comment`'s
 * finding, inherited: `Buffer` is Node-only and this package is bundled into
 * `apps/web` by Vite, which does not polyfill it, while `btoa`/`atob` exist at
 * runtime everywhere but are **not declared** under this workspace's
 * `lib: ["ES2023"]` with no DOM lib, so `tsc` answers `TS2304: Cannot find name
 * 'btoa'`.
 *
 * ⚠ **Copied rather than imported, and the duplication is the point.** These
 * two helpers are module-private in `./comment` — deliberately, so that neither
 * file's codec can be changed from the other. Exporting them to share twenty
 * lines would re-couple exactly what declaring a second codec was meant to keep
 * apart.
 */
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Latin-1 (here: ASCII) in, unpadded base64url out. */
function toBase64Url(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const b0 = input.charCodeAt(i);
    const b1 = input.charCodeAt(i + 1);
    const b2 = input.charCodeAt(i + 2);
    const has1 = !Number.isNaN(b1);
    const has2 = !Number.isNaN(b2);
    const triple = (b0 << 16) | ((has1 ? b1 : 0) << 8) | (has2 ? b2 : 0);
    out += BASE64URL_ALPHABET.charAt((triple >>> 18) & 63);
    out += BASE64URL_ALPHABET.charAt((triple >>> 12) & 63);
    if (has1) out += BASE64URL_ALPHABET.charAt((triple >>> 6) & 63);
    if (has2) out += BASE64URL_ALPHABET.charAt(triple & 63);
  }
  return out;
}

/**
 * Unpadded base64url in, latin-1 out. Throws on a character outside the
 * alphabet and on a length no base64 string can have (`length % 4 === 1`).
 *
 * ⚠ It does **not** throw on every string that merely fails to mean anything —
 * `'abc'` decodes to two bytes of noise quite happily — which is precisely why
 * `decodeNotificationCursor` validates the *shape* afterwards rather than
 * treating a successful decode as proof of anything.
 */
function fromBase64Url(input: string): string {
  if (input.length % 4 === 1) throw new Error('not base64url');
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const character of input) {
    const value = BASE64URL_ALPHABET.indexOf(character);
    if (value < 0) throw new Error('not base64url');
    // Masked to sixteen bits deliberately — `./comment`'s note: only the low
    // twelve are ever read back, and an unmasked accumulator would run past
    // thirty-two bits, where JavaScript's bitwise operators truncate.
    buffer = ((buffer << 6) | value) & 0xffff;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((buffer >>> bits) & 0xff);
    }
  }
  return out;
}

/**
 * Turn the last notification on a page into the cursor that fetches the next
 * one.
 *
 * ⚠ **VALUE-CARRYING and self-contained — base64url of
 * `` `${createdAt}|${id}` `` — and emphatically NOT `?before=<notificationId>`.**
 * The reason is sharper here than it was for comments: `fk_notifications_
 * board_id` and `fk_notifications_card_id` are `ON DELETE CASCADE`, and
 * `fk_notifications_recipient_user_id` is too, so a boundary row can vanish
 * between two pages without any route having deleted it. A cursor that named a
 * row would then resolve to nothing, and the route would have to choose between
 * a 404 on "Load more" and silently restarting at the top. This function takes
 * **no database handle** — that is the property, not an accident.
 *
 * ⚠ **`createdAt` is carried as the exact string the driver returned**, in
 * Postgres' spelling, which is why the route's `WHERE` casts it
 * (`$cursorCreatedAt::timestamptz`). `./comment` carries the measured
 * correction about what that cast does and does not defend against; it applies
 * unchanged.
 *
 * ⚠ **Encode from the last row KEPT, never from the discarded `limit + 1`th
 * row** the query fetched to discover whether another page exists. From the
 * extra row, every page boundary silently skips exactly one notification.
 */
export function encodeNotificationCursor(row: { createdAt: string; id: string }): string {
  const raw = `${row.createdAt}|${row.id}`;
  // Guard the one assumption the codec makes. Both halves are ASCII by
  // construction (a Postgres timestamp and a Crockford base32 ULID), so this
  // can only fire if a caller invents a row — in which case failing loudly
  // beats emitting a cursor that decodes to mojibake.
  for (let i = 0; i < raw.length; i += 1) {
    if (raw.charCodeAt(i) > 0xff) {
      throw new Error('notification cursor components must be latin-1');
    }
  }
  return toBase64Url(raw);
}

/**
 * Turn a cursor back into the `(created_at, id)` pair it names.
 *
 * ⚠ **A MALFORMED CURSOR IS A 422, NEVER "START FROM THE TOP".** Silently
 * falling back to the first page turns a "Load more" into an infinite loop that
 * re-serves page one for ever — a 200 with a well-formed body, so nothing
 * anywhere fails. That is why this throws an `ApiError('validation_failed')`
 * rather than a plain `Error` a route must remember to catch: the rejected
 * alternative leaves the refusal a matter of handler discipline, and a
 * forgotten `try` is a **500** instead of the 422.
 *
 * ⚠ **The shape check after the decode is the whole defence, because base64
 * decoding does not fail on garbage.** `'abc'` is legal base64url and decodes
 * to two bytes of noise; without the checks below the route would build
 * `WHERE (created_at, id) < ('<noise>'::timestamptz, '<noise>')` and Postgres
 * would answer 500 on `invalid input syntax for type timestamp with time zone`.
 * So all four of these are refusals: a character outside the alphabet, an
 * impossible length, a decoded string that does not split into **exactly two**
 * parts on `|`, and a second part that is not a ULID.
 *
 * `|` is safe as the separator because neither component can contain one — a
 * `timestamptz` renders as digits, hyphens, colons, a dot, a space and a sign,
 * and Crockford base32 is `[0-9A-HJKMNP-TV-Z]`.
 */
export function decodeNotificationCursor(cursor: string): {
  createdAt: string;
  id: string;
} {
  const invalid = (): never => {
    throw new ApiError('validation_failed', 'Malformed cursor', {
      cursor: ['must be a cursor returned by a previous page of this drawer'],
    });
  };

  let raw: string;
  try {
    raw = fromBase64Url(cursor);
  } catch {
    return invalid();
  }

  const parts = raw.split('|');
  if (parts.length !== 2) return invalid();

  const parsed = notificationCursorPayloadSchema.safeParse({
    createdAt: parts[0],
    id: parts[1],
  });
  if (!parsed.success) return invalid();

  return parsed.data;
}

// ---------------------------------------------------------------------------
// The two reads and the one write.
// ---------------------------------------------------------------------------

/**
 * `GET /notifications` — `FR-NOTIF-006`, the drawer listing.
 *
 * An object rather than a bare array, for `commentStreamSchema`'s reason: the
 * response carries the next cursor beside the rows and a bare array has nowhere
 * to put it. Unlike the comment stream it carries **no permission fields** —
 * see the header's clause (a): the recipient scope *is* the permission model,
 * so there is no per-viewer answer to state.
 *
 * **Ordering is newest first**, `ORDER BY notifications.created_at DESC,
 * notifications.id DESC`. ⚠ **The `id DESC` tie-break is not decoration**:
 * `created_at` defaults to `now()`, which inside a transaction is the
 * *transaction* timestamp, so a `board.closed` fan-out writing one row per board
 * member writes them all with the **identical** timestamp — a guaranteed tie,
 * not a rare one, and the first place this block will hit it. `id` is the
 * primary key, so `(created_at, id)` is a total order; without it a page
 * boundary can duplicate or skip a row with a 200 and a well-formed body.
 */
export const notificationPageSchema = z.object({
  /** Newest first. `[]` is a legitimate answer and means a quiet account. */
  notifications: z.array(notificationSchema),
  /**
   * The cursor for the next (older) page, or `null` at the end of history.
   *
   * ⚠ **`null` when there is no `limit + 1`th row — NOT "when the page was
   * short".** Emitting a cursor whenever the page came back full gives a "Load
   * more" that returns an empty page at every exact multiple of `limit`, which
   * a client renders as a button that does nothing.
   *
   * ⚠ **And the retention window must be inside the SAME `WHERE` as the cursor
   * comparison.** Applied afterwards — in TypeScript, or in a query wrapping
   * this one — the `LIMIT n + 1` probe becomes window-*unaware*: the extra row
   * can be one the window excludes, so a cursor is emitted at the window floor
   * and "Load more" returns nothing. `FE-17.4`'s end-of-history state is this
   * field being `null`, and that is the only unambiguous signal it has.
   */
  nextCursor: notificationCursorSchema.nullable(),
});

export type NotificationPage = z.infer<typeof notificationPageSchema>;

/**
 * `GET /notifications/unread-count` — `FR-NOTIF-005`, the bell badge.
 *
 * ⚠ **`count(*)::int` in SQL.** `count(*)` is `bigint`, which `pg` returns as a
 * **string**; without the cast this schema 500s on it — and a permissive schema
 * would be worse, shipping `"12"` to a badge that renders it fine and to
 * arithmetic that does not. B13 hit this first and named it.
 *
 * ⚠ **The server returns the TRUE count.** `FR-NOTIF-005`'s "99+" is a
 * **display** cap and belongs to `FE-17.2`; capping server-side would make the
 * number unusable for anything else and would be indistinguishable from a bug
 * at exactly 99.
 *
 * ⚠ **The window here must be the same predicate the listing uses** — see
 * `NOTIFICATION_RETENTION_DAYS`. This is the block's quietest failure and the
 * only assertion that catches it checks both surfaces in one test.
 */
export const unreadCountSchema = z.object({
  count: z.number().int().min(0),
});

export type UnreadCount = z.infer<typeof unreadCountSchema>;

/**
 * `GET /notifications`'s query string.
 *
 * ⚠ **`z.strictObject`, and it has teeth here for the same reason it does on
 * the comment stream**: a plain `z.object()` in Zod 4 **strips** unknown keys,
 * so a typo'd `?curser=` would answer 200 having silently ignored it, and the
 * visible symptom is a "Load more" that re-serves page one for ever.
 *
 * ⚠ **One spelling for the unread filter: `unread`.** The query parameter, the
 * parsed field and the SQL bind are all `unread`; there is no `unreadOnly`
 * anywhere in this block. Two spellings for one flag is how a route ends up
 * reading the parameter it does not parse.
 */
export const notificationQuerySchema = z.strictObject({
  cursor: notificationCursorSchema
    .optional()
    .describe(
      'An opaque cursor from a previous response’s nextCursor. Omit it for the ' +
        'first (newest) page. A malformed cursor is a 422, never a silent ' +
        'restart from the top.',
    ),
  /**
   * ⚠ **`z.coerce.number()`, and the ordering is MEASURED rather than
   * assumed** — re-measured for this block against this schema rather than
   * inherited from `./comment`, on the repository's own rule. Under Zod 4 the
   * coercion runs **before** `.int()`, `.min()` and `.max()`, so the bounds
   * apply to the number and not to the string's length. `?limit=50` → `50`;
   * `?limit=0` → `too_small`; `?limit=101` → `too_big`; `?limit=abc` →
   * `invalid_type` (`Number('abc')` is `NaN`); `?limit=` → `too_small`
   * (`Number('')` is `0`, **not** a type error); `?limit=1.5` →
   * `invalid_type` (from `.int()`). `.default()` is evaluated before the
   * coercion, so an omitted `?limit` yields the default rather than
   * `Number(undefined)`, which is `NaN`.
   */
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(NOTIFICATION_PAGE_SIZE_MAX)
    .default(NOTIFICATION_PAGE_SIZE_DEFAULT)
    .describe(
      'How many notifications to return, 1 to 100, default 25. Coerced from ' +
        'the query string, so the bounds apply to the number rather than to ' +
        'the string’s length.',
    ),
  /**
   * ⚠ **`z.stringbool` with the sets PINNED, and NEVER `z.coerce.boolean()`.**
   * `Boolean('false')` is `true`, so `?unread=false` under `z.coerce.boolean()`
   * filters the drawer to unread only — a 200, a well-formed body, and a filter
   * doing the opposite of what the caller asked. Pinning the sets to exactly
   * two values also makes `?unread=1`, `?unread=yes` and `?unread=ture` 422s,
   * which is what `z.strictObject` on a querystring is for. **Measured**, not
   * assumed: an unpinned `z.stringbool()` accepts `1`, `yes`, `on`, `y` and
   * `enabled`.
   *
   * ⚠ **It is case-INSENSITIVE even with the sets pinned — `?unread=TRUE`
   * parses as `true`.** Measured against Zod 4.4 for this block; `case:
   * 'sensitive'` would refuse it. The looser spelling is kept on purpose (a
   * capitalised query parameter is a client's mistake, not a semantic
   * ambiguity, and it cannot mean the wrong thing the way `Boolean('false')`
   * can), but it is **asserted** in `notification.test.ts` so it is a recorded
   * behaviour rather than a later discovery.
   *
   * `false` by default: the drawer shows read and unread alike, and
   * `?unread=true` is the filter `FR-NOTIF-006` offers.
   */
  unread: z
    .stringbool({ truthy: ['true'], falsy: ['false'] })
    .default(false)
    .describe(
      'true to return only unread notifications, false (the default) for all ' +
        'of them. Exactly the strings “true” and “false” — anything else is a ' +
        '422 rather than a silently coerced filter.',
    ),
});

export type NotificationQuery = z.infer<typeof notificationQuerySchema>;

/**
 * `PATCH /notifications/:id/read` — `FR-NOTIF-007`. SC-4, field-scoped.
 *
 * ⚠ **One field, `{read: boolean}`, and NOT two routes.**
 * `POST /notifications/:id/read` plus `POST /notifications/:id/unread` would be
 * two routes for one column; `PUT /notifications/:id` would put `type` and
 * `groupedCount` in a request body a caller must never author. `read: false` is
 * a real gesture — `FR-NOTIF-007` offers "mark as unread" explicitly — so this
 * is a toggle rather than a one-way acknowledgement.
 *
 * ⚠ **Idempotent, and the idempotence lives in the `UPDATE`, not here.**
 * `FR-NOTIF-007`'s edge case: "a double click landing as two mark-read calls
 * decrements the count once." The route writes `read_at = CASE WHEN $read THEN
 * coalesce(read_at, now()) ELSE NULL END`; a bare `now()` would also decrement
 * once but would move the timestamp on every click, and `coalesce` keeps the
 * first. A schema cannot express that — what it can do is refuse to let the
 * caller send a timestamp at all, which is why there is no `readAt` here.
 *
 * ⚠ **This is the one mutation in the server that does NOT call
 * `withTransaction`, and it writes no activity entry.** Argued rather than
 * assumed, because it looks like an SC-1 violation and is not: read state is
 * per-user, unobservable by anyone else, and `FR-NOTIF-007` says it "is never
 * changed by another user's actions", so SC-1's justification — *an event
 * history that was never recorded cannot be recovered* — is an **audit**
 * argument that does not reach it. Structurally it is inexpressible anyway:
 * `ck_activity_entries_scope` requires a `card_id`, a `board_id`, or one of six
 * `user.`/`workspace.` types with an actor, and a notification read toggle
 * satisfies **none** of the three. A later reader who concludes otherwise
 * should say so loudly — it means a migration widening that CHECK and a new
 * event name, not a quiet `emit()` that fails at runtime on the first bell
 * click.
 */
export const notificationReadPatchSchema = z.strictObject({
  read: z
    .boolean()
    .describe(
      'true to mark the notification read, false to mark it unread. Both ' +
        'directions are idempotent: marking an already-read notification read ' +
        'changes nothing and leaves the original read time in place.',
    ),
});

export type NotificationReadPatch = z.infer<typeof notificationReadPatchSchema>;

// ---------------------------------------------------------------------------
// B29 — email delivery preferences, and the bulk read.
//
// `FR-NOTIF-008` (mark every notification read), `FR-NOTIF-010` (how often
// notification email is sent) and `FR-NOTIF-011` (which categories are emailed
// at all). Three routes under `/me/*` on
// `apps/server/src/routes/notification-preferences.ts`, plus one on the shipped
// `apps/server/src/routes/notifications.ts`.
//
// ⚠⚠ BOTH VOCABULARIES BELOW ARE TRANSCRIBED FROM LIVE CHECK CONSTRAINTS, IN
// THE CONSTRAINT'S OWN ORDER, AND A TYPO IN EITHER IS NOT A TYPE ERROR. It is a
// `new row for relation "…" violates check constraint "ck_…"` — a **500** at
// the moment somebody changes a setting, not at compile time and not in
// `pnpm typecheck`. The values are the only thing in this section that cannot
// be derived from anything else in the repository, so they are what to check
// first when a preference route fails.
//
// ⚠ **No Zod assertion can compare either list against its constraint** — this
// package has no database handle, deliberately (`./mention`'s split: a schema
// that needed a connection would not be importable from `apps/web`). What
// `notification.test.ts` pins is the list as **literals**, so a typo is a red
// unit test in the package that owns the spelling; what the integration suite
// pins is the comparison, by round-tripping every value through the routes.
// Neither alone is sufficient and the two catch different failures.
//
// ⚠ **These preferences govern EMAIL ONLY.** `FR-NOTIF-011` is explicit — "the
// in-app drawer still receives every notification" — so nothing here belongs in
// `apps/server/src/notifications/generate.ts`. A category filter applied there
// would delete the drawer row as well as the email, and the symptom would be a
// bell that quietly under-counts for anybody who ever turned a category off.
// ---------------------------------------------------------------------------

/**
 * `ck_user_preferences_notification_email_frequency`, verbatim and in order —
 * `user_preferences.notification_email_frequency varchar(32) NOT NULL DEFAULT
 * 'periodically'`.
 *
 * `FR-NOTIF-010`'s three settings, and the column's **`DEFAULT` is the
 * requirement's stated default**, which is why B29 ships no migration for it:
 * `0001_identity_and_workspaces.sql` already recorded the answer. A user with
 * no `user_preferences` row at all is therefore `periodically` too — the route
 * synthesises the same value the column would have supplied, rather than
 * inventing a fourth "unset" state the client would have to render.
 *
 * ⚠ **`never` is a real setting and not the absence of one**, which matters to
 * whoever writes the sweep: it is excluded by the frequency filter and issues
 * **no query**, rather than producing an empty digest that then has to be
 * suppressed. `instantly` and `periodically` share one predicate
 * (`email_sent_at IS NULL AND read_at IS NULL`) and differ only in how long the
 * oldest pending notification must have waited.
 */
export const NOTIFICATION_EMAIL_FREQUENCIES = [
  /**
   * `FR-NOTIF-010`'s "a short deliberate delay between the in-app notification
   * and the email" — not zero. The delay is what makes the requirement's
   * read-state re-check ("if the user has already read it in-app, the email is
   * dropped") able to catch anything at all; at zero there is no window in
   * which to read it.
   */
  'instantly',
  /** The default, in the requirement and in the column: an hourly digest. */
  'periodically',
  /** No notification email of any kind. Not a category opt-out; a global off. */
  'never',
] as const;

/**
 * `z.enum` over the frequency vocabulary.
 *
 * ⚠ **No `NOTIFICATION_EMAIL_FREQUENCY_MAX_LENGTH` constant, deliberately**,
 * although the column is `varchar(32)` exactly as `notifications.type` is.
 * `./card`'s rule (import the constant describing *your* column if one exists,
 * declare it if none does, never share one across two) has a precondition this
 * case fails: a bound is worth declaring when a **value can be built at
 * runtime** and overflow the column. This vocabulary is three closed literals
 * that can only change by editing a CHECK inside a migration, which is where
 * the width is decided anyway. `notification.test.ts` asserts all three fit,
 * against the literal 32 and with the column named, rather than borrowing
 * `NOTIFICATION_TYPE_MAX_LENGTH` — which describes a **different column** and
 * would couple two widths that are free to diverge.
 */
export const notificationEmailFrequencySchema = z.enum(NOTIFICATION_EMAIL_FREQUENCIES);

export type NotificationEmailFrequency = z.infer<typeof notificationEmailFrequencySchema>;

/**
 * `ck_notification_email_category_prefs_category`, verbatim and in order —
 * `notification_email_category_prefs.category varchar(32)`, primary key
 * `(user_id, category)`, `enabled boolean NOT NULL DEFAULT true`.
 *
 * `FR-NOTIF-011`'s seven categories. The requirement says "at least" these
 * seven, and the CHECK admits exactly them, so the constraint is the closed
 * list until a migration says otherwise.
 *
 * ⚠⚠ **THESE ARE NOT NOTIFICATION TYPES AND THE TWO VOCABULARIES MUST NOT BE
 * CONFLATED — they are not even the same size.** Eighteen registered types map
 * onto seven categories **and a null**, in
 * `apps/server/src/notifications/email/categories.ts`, and `null` is the
 * load-bearing entry: it means *always emailed and not opt-out-able*, which is
 * `FR-NOTIF-011`'s "direct mentions and invitations to a board or workspace are
 * always emailed and cannot be opted out of here". `removed_from_card` is the
 * one string that appears in **both** vocabularies with the same spelling and
 * a different meaning — a notification type here, a category there — and it is
 * the single likeliest place for a `NotificationType` to be passed where an
 * `EmailCategory` was wanted. TypeScript catches it because both are real
 * unions; nothing else would.
 *
 * ⚠ **A category with no row means ENABLED, not disabled.** The column defaults
 * `true` and `FR-NOTIF-011` opts a user *out* rather than *in*, so the absence
 * of a row is the on state. `notificationPreferencesSchema` below is what keeps
 * a client from ever needing to know that.
 */
export const NOTIFICATION_EMAIL_CATEGORIES = [
  /** `watched_comment_added`. */
  'comments',
  /**
   * `watched_due_date_changed`, `watched_completion_toggled`, `due_soon` and
   * (when B26's type is registered) `checklist_item_due_changed`.
   *
   * ⚠ **Completion under "due dates" is a judgement call, recorded as one.**
   * `FR-NOTIF-011` names seven categories "at least" and completion is not one
   * of them; it is emitted by `due.date_completed` / `due.date_uncompleted` and
   * is a due-date concept in this schema's own vocabulary. The alternative —
   * leaving it uncategorised — would make it un-opt-out-able, which
   * contradicts the requirement's spirit for a type that is not a mention and
   * not an invitation.
   */
  'due_dates',
  /**
   * `removed_from_card` the **notification type** — the one name shared with
   * the other vocabulary. See the ⚠⚠ above.
   */
  'removed_from_card',
  /** `watched_attachment_added`. */
  'attachments_added',
  /** `watched_card_created_in_list` and `watched_card_created_on_board`. */
  'cards_created',
  /** `watched_card_moved`. */
  'cards_moved',
  /** `watched_card_archived` — both directions of it; see that type's entry. */
  'cards_archived',
] as const;

/**
 * `z.enum` over the category vocabulary. See
 * `notificationEmailFrequencySchema` for why there is no length constant.
 */
export const notificationEmailCategorySchema = z.enum(NOTIFICATION_EMAIL_CATEGORIES);

export type NotificationEmailCategory = z.infer<typeof notificationEmailCategorySchema>;

/**
 * `ck_mobile_push_prefs_category`, verbatim and in order. These are mobile-push
 * categories, not notification types and not email categories.
 */
export const NOTIFICATION_MOBILE_PUSH_CATEGORIES = [
  'added_to_card',
  'removed_from_card',
  'comment_on_card',
  'attachment_added',
  'card_changed',
  'card_created',
  'added_to_board',
  'removed_from_board',
  'made_board_admin',
  'board_closed',
  'added_to_workspace',
  'removed_from_workspace',
  'made_workspace_admin',
  'mentioned',
  'due_date_approaching',
] as const;

export const notificationMobilePushCategorySchema = z.enum(
  NOTIFICATION_MOBILE_PUSH_CATEGORIES,
);

export type NotificationMobilePushCategory = z.infer<
  typeof notificationMobilePushCategorySchema
>;

export const NOTIFICATION_MOBILE_PUSH_PLATFORMS = ['ios', 'android', 'web'] as const;

export const mobilePushPlatformSchema = z.enum(NOTIFICATION_MOBILE_PUSH_PLATFORMS);

export type MobilePushPlatform = z.infer<typeof mobilePushPlatformSchema>;

export const NOTIFICATION_MOBILE_PUSH_PROVIDERS = ['apns', 'fcm', 'web_push'] as const;

export const mobilePushProviderSchema = z.enum(NOTIFICATION_MOBILE_PUSH_PROVIDERS);

export type MobilePushProvider = z.infer<typeof mobilePushProviderSchema>;

export const NOTIFICATION_MOBILE_PUSH_PERMISSION_STATUSES = [
  'granted',
  'denied',
  'revoked',
] as const;

export const mobilePushPermissionStatusSchema = z.enum(
  NOTIFICATION_MOBILE_PUSH_PERMISSION_STATUSES,
);

export type MobilePushPermissionStatus = z.infer<typeof mobilePushPermissionStatusSchema>;

export const NOTIFICATION_MOBILE_PUSH_TOKEN_MAX_LENGTH = 512;

/**
 * `GET /me/notification-preferences` — `FR-NOTIF-010` and `FR-NOTIF-011` read
 * together, extended by B77's `FR-NOTIF-013` mobile-push categories because the
 * settings panel renders notification channels together.
 *
 * ⚠⚠ **EVERY CATEGORY IS PRESENT ON EVERY RESPONSE, ALWAYS — the server
 * projects the seven, it does not return the rows it found.** A user who has
 * never touched a toggle has **no** `notification_email_category_prefs` rows at
 * all, and returning the rows would answer `{}`; the client would then have to
 * know that a missing key means *enabled*, and every client that forgot would
 * render seven switches in the off position for a user whose email is entirely
 * on. That is a wrong screen with a 200 and a well-formed body — the failure
 * class this package exists to make unrepresentable — so the rule is stated
 * here in the contract rather than left to the route.
 *
 * ⚠ **`emailCategories` is `z.record` over the enum, and the behaviour was
 * MEASURED against this repository's pinned Zod (4.4.3) rather than assumed.**
 * A record keyed by a `z.enum` is **exhaustive and strict**: a missing key is
 * an `invalid_type` issue and an unrecognised key is `unrecognized_keys` — it
 * does **not** strip the way `z.object` does, and it does **not** treat the
 * keys as optional the way `z.record(z.string(), …)` would. Both halves are
 * load-bearing and both are pinned in `notification.test.ts`:
 *
 *   - **Exhaustive** is what makes the paragraph above enforceable. A route
 *     that projected six categories and forgot `cards_moved` fails
 *     serialisation instead of shipping a screen with one switch missing.
 *   - **Strict** is what stops a route echoing a raw `category` column value
 *     the CHECK admits but this list has not registered.
 *
 * ⚠ **And therefore NO `.default(true)` on the values.** A per-key default
 * would make exactly the omission described above **invisible** — the response
 * would parse, every missing category would materialise as `true`, and a route
 * that lost a category would be indistinguishable from a user who had enabled
 * it. The "defaulted true" in this block's design is a statement about the
 * **database row** (absent means enabled), and it is the *route's* job to
 * apply, not the schema's. `./card-dates` draws the same line for the reminder
 * offset: a default that belongs to a column does not belong on the wire.
 *
 * ⚠ **This is the second shape in the package whose whole payload is per-user**
 * — `notificationSchema` is the first — and unlike `checklistSchema.hideChecked`
 * (the first *field* whose value depends on who asked) there is no shared row
 * behind it at all. Nothing here is cacheable across accounts, and nothing here
 * is observable by another account: `FR-NOTIF-011`'s toggles are as invisible
 * to other members as `FR-COLLAB-022`'s watches are.
 */
export const notificationPreferencesSchema = z.object({
  /**
   * `user_preferences.notification_email_frequency`, or `'periodically'` when
   * the caller has no `user_preferences` row — see
   * `NOTIFICATION_EMAIL_FREQUENCIES`. Not nullable and not optional: there is
   * no "unset" state a client could render, because the column's `DEFAULT` is
   * the requirement's default.
   */
  emailFrequency: notificationEmailFrequencySchema,
  /**
   * All seven categories, `true` where the caller has no row and `true`/`false`
   * from `notification_email_category_prefs.enabled` where they do.
   *
   * ⚠ **A record rather than an array of `{category, enabled}` objects**, and
   * the difference is that a record cannot express a duplicate. `(user_id,
   * category)` is the table's primary key, so a category appears at most once
   * by construction; an array would put a shape on the wire that can represent
   * a state the database refuses, and a client merging it would have to decide
   * which of two conflicting entries wins.
   */
  emailCategories: z.record(notificationEmailCategorySchema, z.boolean()),
  /**
   * All fifteen mobile-push categories, `true` where the caller has no row and
   * `true`/`false` from `mobile_push_prefs.enabled` where they do.
   */
  mobilePushCategories: z.record(notificationMobilePushCategorySchema, z.boolean()),
});

export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/**
 * `PATCH /me/notification-preferences/email-frequency` — `FR-NOTIF-010`, SC-4.
 *
 * ⚠ **Field-scoped, and NOT one `PATCH /me/notification-preferences` taking
 * both halves.** `user_preferences` has twenty-three columns and this route may
 * touch exactly one of them; an object patch would put `theme`,
 * `planner_working_days` and `inbox_email_address` in a body this route's
 * caller must never author, and a concurrent edit from the appearance panel
 * would be clobbered by whatever the notification panel last read. That is
 * `profile.ts`'s established shape — `/me/bio`, `/me/display-name`,
 * `/me/avatar`, one route per **field** — followed rather than re-derived.
 *
 * ⚠ **`z.strictObject`**, because this is an input: a typo'd key must be a 422
 * rather than a silently ignored setting that appears to save and does not.
 * `notificationReadPatchSchema` above draws the same line for the same reason.
 */
export const notificationEmailFrequencyPatchSchema = z.strictObject({
  emailFrequency: notificationEmailFrequencySchema.describe(
    'How often notification email is sent: instantly (after a short delay, ' +
      'and only if still unread), periodically (an hourly digest of what has ' +
      'accumulated) or never. Exactly one of those three strings.',
  ),
});

export type NotificationEmailFrequencyPatch = z.infer<
  typeof notificationEmailFrequencyPatchSchema
>;

/**
 * `PATCH /me/notification-preferences/email-categories/:category` —
 * `FR-NOTIF-011`, SC-4.
 *
 * ⚠ **The category is in the PATH and only `enabled` is in the body**, which is
 * what keeps this field-scoped rather than a seven-field object patch. Sending
 * the whole map would make two panels open in two tabs clobber each other's
 * toggles — the same failure `/me/display-name` and `/me/bio` were split to
 * avoid — and would give the route no way to distinguish "leave this one alone"
 * from "turn this one off".
 *
 * ⚠ **`:category` is validated by the ROUTE, against
 * `notificationEmailCategorySchema`, and there is no params schema here** —
 * `./notification`'s standing rule: a route's URL shape lives at the route
 * module, beside `commentParams` and `cardParams`, and never in a package the
 * browser also imports. An unregistered category must be a **404**, not a 422:
 * `/me/notification-preferences/email-categories/nonsense` names no resource.
 *
 * ⚠ **`enabled`, not `disabled`.** The column is `enabled boolean NOT NULL
 * DEFAULT true`, and one spelling for one flag is what stops a route reading
 * the field it does not parse — `notificationQuerySchema`'s `unread` note, in
 * as many words.
 */
export const notificationEmailCategoryPatchSchema = z.strictObject({
  enabled: z
    .boolean()
    .describe(
      'true to receive email for this category, false to opt out of it. ' +
        'Opting out affects email only — the in-app drawer still receives ' +
        'every notification (FR-NOTIF-011).',
    ),
});

export type NotificationEmailCategoryPatch = z.infer<
  typeof notificationEmailCategoryPatchSchema
>;

/**
 * `PATCH /me/notification-preferences/mobile-push-categories/:category` —
 * `FR-NOTIF-013`, SC-4. Push toggles suppress push delivery only.
 */
export const notificationMobilePushCategoryPatchSchema = z.strictObject({
  enabled: z
    .boolean()
    .describe(
      'true to receive mobile push for this category, false to suppress push. ' +
        'This affects push only; in-app notifications and email settings are independent.',
    ),
});

export type NotificationMobilePushCategoryPatch = z.infer<
  typeof notificationMobilePushCategoryPatchSchema
>;

/**
 * `PUT /me/mobile-push-installations` — register or refresh one app install.
 * The provider token is opaque and compared exactly by the server.
 */
const mobilePushInstallationMutableFields = {
  providerToken: z.string().min(1).max(NOTIFICATION_MOBILE_PUSH_TOKEN_MAX_LENGTH),
  permissionStatus: mobilePushPermissionStatusSchema.exclude(['revoked']),
};

/**
 * A Web Push subscription's endpoint (RFC 8030) — the `web_push` arm's
 * `providerToken`, and the body of the revoke route.
 *
 * ⚠ **`https:` only, and at most 512 characters.** The endpoint is a URL the
 * worker POSTs to, so it is an outbound-request surface the caller authors;
 * every browser push service issues an `https:` endpoint, and nothing else has
 * a reason to be here. The 512 bound is the column's width
 * (`mobile_push_installations.provider_token varchar(512)`): an endpoint that
 * long is refused at the contract rather than truncated or widened into the
 * column (`0069`).
 */
export const webPushEndpointSchema = z
  .url({ protocol: /^https$/ })
  .max(NOTIFICATION_MOBILE_PUSH_TOKEN_MAX_LENGTH);

/** Unpadded or padded base64url, as `PushSubscription.toJSON()` produces. */
const base64UrlPattern = /^[A-Za-z0-9_-]+={0,2}$/;

/**
 * `PushSubscription.toJSON().keys` — the subscriber's P-256 ECDH public key
 * (65 bytes uncompressed → 87 base64url characters, 88 padded) and its auth
 * secret (16 bytes → 22, 24 padded). Bounded to those widths so a key the
 * sender could never encrypt to is a 422 here rather than a failed send later;
 * both fit the `0069` columns (`varchar(128)`, `varchar(64)`).
 */
export const webPushSubscriptionKeysSchema = z.strictObject({
  p256dh: z.string().min(87).max(88).regex(base64UrlPattern),
  auth: z.string().min(22).max(24).regex(base64UrlPattern),
});

export type WebPushSubscriptionKeys = z.infer<typeof webPushSubscriptionKeysSchema>;

export const mobilePushInstallationPutSchema = z.discriminatedUnion('provider', [
  z.strictObject({
    platform: z.literal('ios'),
    provider: z.literal('apns'),
    ...mobilePushInstallationMutableFields,
  }),
  z.strictObject({
    platform: z.enum(['ios', 'android']),
    provider: z.literal('fcm'),
    ...mobilePushInstallationMutableFields,
  }),
  /**
   * Web Push (`0069`). `providerToken` is the subscription **endpoint** and
   * nothing else — the server hashes it as it hashes a native token, so a
   * re-subscribe that keeps its endpoint and rotates its keys updates the same
   * row. `keys` is required here and refused (strict) on the native arms.
   */
  z.strictObject({
    platform: z.literal('web'),
    provider: z.literal('web_push'),
    providerToken: webPushEndpointSchema,
    permissionStatus: mobilePushInstallationMutableFields.permissionStatus,
    keys: webPushSubscriptionKeysSchema,
  }),
]);

export type MobilePushInstallationPut = z.infer<typeof mobilePushInstallationPutSchema>;

/**
 * `GET /me/web-push-config` — the VAPID public key the browser passes to
 * `pushManager.subscribe` as `applicationServerKey`. `null` when this instance
 * has no VAPID key pair configured, which is how a client knows to hide the
 * control rather than offer a subscription nothing will ever send to.
 */
export const webPushConfigSchema = z.object({
  publicKey: z.string().min(1).nullable(),
});

export type WebPushConfig = z.infer<typeof webPushConfigSchema>;

/**
 * `POST /me/web-push-subscription/revoke` — revoke the caller's `web_push`
 * installation for this endpoint. Addressed by endpoint rather than by
 * installation id because the browser knows its subscription and not the
 * server's row id; idempotent, and a no-op for an endpoint the caller does
 * not own. Answers with the installation list, as the other install routes do.
 */
export const webPushSubscriptionRevokeSchema = z.strictObject({
  endpoint: webPushEndpointSchema,
});

export type WebPushSubscriptionRevoke = z.infer<typeof webPushSubscriptionRevokeSchema>;

export const mobilePushInstallationSchema = z.object({
  id: ulidSchema,
  platform: mobilePushPlatformSchema,
  provider: mobilePushProviderSchema,
  permissionStatus: mobilePushPermissionStatusSchema,
  lastSeenAt: z.string().min(1),
  revokedAt: z.string().min(1).nullable(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export type MobilePushInstallation = z.infer<typeof mobilePushInstallationSchema>;

export const mobilePushInstallationListSchema = z.object({
  installations: z.array(mobilePushInstallationSchema),
});

export type MobilePushInstallationList = z.infer<typeof mobilePushInstallationListSchema>;

/**
 * `POST /notifications/read-all` — `FR-NOTIF-008`, the result of marking every
 * notification read.
 *
 * ⚠ **There is no request shape at all, and that absence is how the route
 * complies with the requirement's edge case**, which is explicit: with the
 * unread-only filter on, the action still applies to **all** notifications
 * rather than the filtered subset. A route that took a filter parameter could
 * be asked to do the other thing; a route that takes nothing cannot. The
 * struck-through bullet in this file's header predicted this shape and
 * predicted that it would arrive with a route rather than ahead of one.
 *
 * ⚠ **`POST`, not `PATCH`** — a bulk action over an unbounded set is not a
 * field on a resource. `PATCH /notifications/:id/read` stays exactly as it is,
 * and there is no radix collision: `/notifications/read-all` is a two-segment
 * static path and `/notifications/:id/read` is three.
 *
 * ⚠⚠ **THE ONE SILENT FAILURE THIS SCHEMA CANNOT PREVENT, STATED HERE BECAUSE
 * IT IS WHERE A READER LOOKS.** The `UPDATE`'s predicate must be the unread
 * count's predicate — `recipient_user_id = $me` AND `read_at IS NULL` AND
 * `withinRetentionWindow()`, the helper already in `routes/notifications.ts`,
 * **reused verbatim rather than re-spelled**. If the two disagree,
 * `FR-NOTIF-008`'s AC1 ("the badge disappears") produces a badge that never
 * clears: the action reports a `markedCount`, returns 200, and leaves rows the
 * count still sees. Nothing is red anywhere. See `NOTIFICATION_RETENTION_DAYS`
 * for the same invariant stated for the listing.
 */
export const markAllNotificationsReadResultSchema = z.object({
  /**
   * How many notifications this call moved from unread to read.
   *
   * ⚠ **`0` is a legitimate answer and must not be a 404 or a 409.** Pressing
   * "mark all read" on an already-empty drawer is a gesture whose intent is
   * already satisfied, exactly as `DELETE` on a watch that is not there is —
   * `./watch`'s note. `.min(0)`, therefore, and not `.min(1)`.
   *
   * ⚠ **From `.returning()`, and an `::int` if it is ever computed as a
   * `count(*)` instead** — `count(*)` is `bigint`, which `pg` hands back as a
   * **string**. `unreadCountSchema` carries the long form of this trap; it is
   * the same one, one route along.
   */
  markedCount: z.number().int().min(0),
});

export type MarkAllNotificationsReadResult = z.infer<
  typeof markAllNotificationsReadResultSchema
>;

/**
 * `GET|PUT|DELETE /boards/:id/silence` — `FR-NOTIF-014`, the caller's private
 * board-silence state.
 *
 * A silence row suppresses notification output from watches on this board, but
 * it is not itself a watch and it does not delete watches. Direct involvement
 * notifications such as mentions still have their own audience and are not
 * represented by this state.
 */
export const boardSilenceStateSchema = z.object({
  boardId: ulidSchema,
  silenced: z.boolean(),
});

export type BoardSilenceState = z.infer<typeof boardSilenceStateSchema>;
